import { useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * BuildVersionWatcher — invisible component that detects when a new build
 * has been deployed and the user is still running the old JS bundle.
 *
 * Background: the service worker (public/sw.js) is intentionally cache-free
 * — no fetch handler, no Cache API writes. But the production edge layer
 * caches `/` (index.html) for 5 minutes (verified: `cache-control: public,
 * max-age=300`), so for up to 5 min after a deploy, users hitting the root
 * URL load the OLD index.html → OLD bundled JS → missing freshly-shipped
 * features (e.g. the dedup card that "didn't appear" after deploy).
 *
 * iter367 restoration: the backend fingerprint was BACKEND-ONLY (only
 * hashed /app/backend/**\/*.py), so frontend-only deploys never flipped
 * it and the "new version available" prompt effectively stopped firing.
 * `routes/health.py::_build_manifest` now also hashes the frontend
 * source tree (or the shipped bundle in production).  This watcher's
 * poll-and-compare logic below stayed correct throughout — restoring
 * the SIGNAL brings the notice back.
 *
 * This watcher polls `/api/health` on a slow cadence (60s + on
 * window-focus + on visibilitychange) and compares the backend's
 * reported `build_fingerprint`. If the fingerprint has changed since
 * the SPA loaded, we surface a single dismissible toast with a
 * Refresh button. The user gets a fast, explicit path to the new
 * bundle without manual hard-refresh.
 *
 * Notes / non-goals:
 *   - Does NOT auto-reload (an unexpected reload would lose in-progress
 *     work like dry-run plans on /admin/import-bundle).
 *   - Only fires once per fingerprint change. Reuses sonner toast `id` so
 *     a flapping backend doesn't stack toasts.
 *   - Only reads `build_fingerprint`; ignores `process_started_at` (which
 *     changes on every container restart and would false-positive).
 */
export default function BuildVersionWatcher() {
  const initialFp = useRef(null);
  const lastNotified = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      if (cancelled) return;
      try {
        const r = await axios.get(`${API}/api/health`, {
          headers: { 'Cache-Control': 'no-cache' },
          // Bypass any browser/HTTP cache; health is cheap.
          params: { _: Date.now() },
        });
        const fp = r?.data?.build_fingerprint;
        if (!fp) return;
        if (initialFp.current == null) {
          initialFp.current = fp;
          // Expose for debugging + easy verification from devtools.
          try { window.__KEYMADE_BOOT_FP = fp; } catch { /* noop */ }
          return;
        }
        if (fp !== initialFp.current && fp !== lastNotified.current) {
          lastNotified.current = fp;
          try { window.__KEYMADE_NEW_FP = fp; } catch { /* noop */ }
          // DP261 — the build moved on. Ask the worker to fetch its new copy
          // so the next navigation is served by the current build. If one is
          // already waiting (our /sw.js skipWaitings on install, so this is
          // rare), let it through and reload once for this fingerprint. The
          // toast below still owns the visible path: no second banner, and no
          // surprise reload while a worker is not waiting.
          try {
            if ('serviceWorker' in navigator) {
              const reg = await navigator.serviceWorker.getRegistration();
              if (reg) {
                await reg.update();
                if (reg.waiting) {
                  const key = `km-sw-skip:${fp}`;
                  const done = sessionStorage.getItem(key) === '1';
                  reg.waiting.postMessage({ type: 'keymade-skip-waiting' });
                  if (!done) {
                    sessionStorage.setItem(key, '1');
                    window.location.reload();
                    return;
                  }
                }
              }
            }
          } catch { /* SW blocked: the toast is the path */ }
          toast(
            'A new version of KEY-MADE is available',
            {
              id: 'keymade-new-version',
              description: 'Refresh to load the latest build.',
              duration: Infinity,
              // Wave 5.9 build 8 — force readable dark text on the light
              // sonner toast (default `descriptionClassName` renders white
              // on the near-white background of the deploy toast).
              className: '!text-slate-900',
              descriptionClassName: '!text-slate-700',
              action: {
                label: 'Refresh',
                onClick: () => {
                  window.location.reload();
                },
              },
            }
          );
        }
      } catch {
        /* network/health hiccup — silent */
      }
    };

    check();
    const interval = setInterval(check, 60_000);
    const onFocus = () => check();
    const onVisibility = () => { if (document.visibilityState === 'visible') check(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return null;
}
